import { ThemeComponent } from "@/theme/ThemeComponent";
import { Component, Prop, Ref } from "@eastsideco/salvo-ts";
import { cart } from '@/theme/Cart';
import type { ICartItem } from "@/theme/cart/types/ICartItem";

import "./ProgressBar.scss"

/**
 * ----------------------------------------------------------------------------
 * Component: ProgressBar
 * ------------------------------------------------------------------------------
 */
@Component
export class ProgressBar extends ThemeComponent  {

    @Prop() freeGift!: boolean;
    @Prop() freeGiftThreshold!: number;
    @Prop() freeGiftId!: number;

    @Ref() indicator!: HTMLElement;
    @Ref() freeGiftText!: HTMLElement;
    @Ref() freeGiftProduct!: HTMLElement;

    private thresholdPercentage: number|null = null;
    private freeGiftInCart: ICartItem|undefined|null = null;

    async init() {
        // Render progress bar on load
        await this.renderProgressBar();

        // Re-render progress bar when the cart changes
        this.$subscribe(cart.onChangeByInteraction, async() => {
            if (this.freeGift && this.freeGiftThreshold && this.freeGiftId) {
                await this.renderProgressBar();
            }
        });
    }

    // eslint-disable-next-line
    async renderProgressBar() {

        // Check if cart has items
        if (cart.items.length > 0) {

            //  Calculate percentage
            this.thresholdPercentage = this.calculateProgress(this.freeGiftThreshold, cart.totalPrice);

            //  Check for free gift already in cart
            this.freeGiftInCart = cart.items.find((item) => item.variantId == this.freeGiftId);

            if (this.thresholdPercentage < 100 && this.freeGiftInCart) {
                // If threshold is not met and a free gift is in cart, remove it
                await cart.removeItem(this.freeGiftInCart).catch((err) => {
                    throw new Error(err);
                });
            } else if (this.thresholdPercentage >= 100 && !this.freeGiftInCart) {
                // If threshold is met and a free gift is not in cart, add it
                await cart.addItem({
                    id: this.freeGiftId,
                    quantity: 1,
                    properties: {
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        _eligibleGWPId: this.freeGiftId,
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        _upsellThresholdAmount: this.freeGiftThreshold
                    }
                }).catch((err) => {
                    throw new Error(err);
                });
            }

            // Show progress bar
            this.showProgressBar();

        }
    }

    calculateProgress(threshold: number, totalPrice: number) {

        // Calculate percentage
        const leftToSpendSum = threshold - (totalPrice / 100);
        let percentage = (totalPrice / (threshold * 100)) * 100;

        // Max percmentage at 100
        percentage = percentage > 100 ? 100 : percentage;

        // Set progress width
        this.indicator.style.width = `${Math.round(percentage)}%`;

        // Add progress text
        if (percentage < 100) {
            if (this.freeGiftText) {
                this.freeGiftText.textContent = `Add at least €${leftToSpendSum.toFixed(2)} more to get a free gift`;
            }
            // Set/remove active classes
            this.freeGiftProduct.classList.remove('is-active');
            this.classList.add('free-gift__progress-bar--active');

        } else {
            // Set/remove active classes
            this.classList.remove('free-gift__progress-bar--active');
            this.freeGiftProduct.classList.add('is-active');
        }

        return percentage;
    }

    hideProgressBar() {
        // Hide the progress bar
        this.classList.remove('free-gift__progress-bar--active');
    }

    showProgressBar() {
        // Dispay the progress bar
        this.classList.add('free-gift__progress-bar--active');
    }
}

